/**
 * Default composition for the "Send Deposit Request" modal (sc-46627), plus the
 * plain-text -> HTML conversion its body needs.
 *
 * Copy is Melissa/design-provided (see the epic's wireframe). Only the subject and body travel:
 * LeanLawEmailService composes the greeting, the CTA button and the expiry footer around them
 * (PayabliDisbursementEmailService.BuildBody). The preview below reproduces that chrome, so a
 * change to either side has to be made on both or the preview starts lying about what is sent.
 */
import { formatCurrency } from '@/services/utils';
import { escapeHtmlText } from '@/utils/editorHtml';

/**
 * Still unverified against Payabli's actual configured default for this account: their docs
 * describe the fallback mechanism without stating a number, and `PaymentLink/load` never
 * surfaces `expirationDate` for an active link. This figure is now stated to the client in the
 * sent email too, so correcting it means changing `Payabli:LinkExpiryDays` in LeanLawEmailService
 * and this constant together — the email reads its own config and never sees this one. sc-46627.
 */
export const LINK_EXPIRY_DAYS = 14;

export const DISBURSEMENT_CTA_LABEL = 'Enter Bank Details & Request Deposit';

export const buildDefaultSubject = (matterName: string): string => {
  const name = (matterName ?? '').trim();
  // A dangling em dash is worse than no suffix when the matter has no name to show.
  return name ? `Your Settlement Disbursement — ${name}` : 'Your Settlement Disbursement';
};

/**
 * ContactDetail.address is a ContactAddress object, not a string. Street on one line,
 * "City, State ZIP" on the next — the same shape the client-contact report builds.
 */
export const formatContactAddress = (address: any): string => {
  if (!address) return '';
  if (typeof address === 'string') return address;

  const cityStateZip = [
    [address.city, address.state].filter(Boolean).join(', '),
    address.zip,
  ]
    .filter(Boolean)
    .join(' ');

  return [address.address, cityStateZip].filter(Boolean).join('\n');
};

export const buildGreeting = (clientName: string): string => `Hi ${clientName},`;

export const buildDefaultBody = (amount: number): string =>
  `Your case has settled and a payment of ${formatCurrency(amount)} is ready to be sent to you. `
  + 'Please follow the secure link below to enter your bank account details so we can deposit '
  + 'your funds directly.';

/**
 * LeanLawEmailService assigns the stored body straight to SendGrid's HtmlContent, so a
 * textarea's newlines would collapse and the client would receive one run-on paragraph.
 * Blank lines become paragraphs, single newlines become breaks.
 */
export const toEmailHtml = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return '';
  return trimmed
    .split(/\n\s*\n/)
    .map((block) => `<p>${escapeHtmlText(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
};

/**
 * The inverse of toEmailHtml, so a stored body can go back into the modal's textarea for a resend.
 * A string transform over toEmailHtml's own output, deliberately not a DOM parse: the input is
 * server data, and it ends up as a v-model value, never as markup.
 */
export const fromEmailHtml = (html: string): string => {
  const trimmed = (html ?? '').trim();
  if (!trimmed) return '';
  const blocks = /^<p>[\s\S]*<\/p>$/.test(trimmed)
    ? trimmed.slice(3, -4).split('</p><p>')
    : [trimmed];
  return blocks
    .map((block) => block
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&amp;/g, '&'))
    .join('\n\n');
};

/**
 * Normalizes a pasted recipient list to the single shape the shared email regex accepts and the
 * backend splits on: comma, one space. Semicolons are tolerated on the way in because that is
 * what Outlook and the Windows address book hand over.
 */
export const normalizeRecipients = (value: string): string =>
  value
    .split(/[,;]/)
    .map((address) => address.trim())
    .filter(Boolean)
    .join(', ');

/** Rejects the CR/LF and control characters that would forge an email header. */
export const hasHeaderInjectionRisk = (value: string): boolean =>
  // eslint-disable-next-line no-control-regex
  /[\r\n\u0000-\u001F\u007F]/.test(value);
